import { useRef, useState, useCallback } from "react";
import { API } from "@/lib/api";
import { toast } from "sonner";

const pickMime = () =>
  ["audio/webm;codecs=opus", "audio/webm", "audio/mp4", "audio/ogg"].find(
    (t) => window.MediaRecorder?.isTypeSupported?.(t)
  ) || "";

const SILENCE_MS = 1500;
const NO_SPEECH_MS = 8000;
const SPEECH_RMS = 0.02;

function watchSilence(stream, onSilence, onNoSpeech) {
  const Ctx = window.AudioContext || window.webkitAudioContext;
  if (!Ctx) return () => {};
  const ctx = new Ctx();
  const analyser = ctx.createAnalyser();
  analyser.fftSize = 1024;
  ctx.createMediaStreamSource(stream).connect(analyser);
  const buf = new Float32Array(analyser.fftSize);
  const t0 = Date.now();
  let heard = false;
  let lastVoice = t0;
  const id = setInterval(() => {
    analyser.getFloatTimeDomainData(buf);
    const rms = Math.sqrt(buf.reduce((a, v) => a + v * v, 0) / buf.length);
    const now = Date.now();
    if (rms > SPEECH_RMS) { heard = true; lastVoice = now; }
    if (heard && now - lastVoice > SILENCE_MS) { clearInterval(id); onSilence(); }
    else if (!heard && now - t0 > NO_SPEECH_MS) { clearInterval(id); onNoSpeech(); }
  }, 100);
  return () => { clearInterval(id); ctx.close().catch(() => {}); };
}

export function useVoice() {
  const [recording, setRecording] = useState(null); // "dictate" | "talk" | null
  const [transcribing, setTranscribing] = useState(false);
  const [speaking, setSpeaking] = useState(false);
  const recRef = useRef(null);
  const audioRef = useRef(null);

  const start = useCallback(async (mode, { onSilence, onNoSpeech } = {}) => {
    if (!navigator.mediaDevices?.getUserMedia || !window.MediaRecorder) {
      toast.error("Voice input isn't supported in this browser.");
      return false;
    }
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      const mime = pickMime();
      const rec = new MediaRecorder(stream, mime ? { mimeType: mime } : undefined);
      const chunks = [];
      rec.ondataavailable = (e) => e.data.size && chunks.push(e.data);
      recRef.current = { rec, stream, chunks, mime: rec.mimeType || mime || "audio/webm" };
      rec.start();
      if (onSilence) recRef.current.unwatch = watchSilence(stream, onSilence, onNoSpeech || onSilence);
      setRecording(mode);
      return true;
    } catch {
      toast.error("Microphone access was denied.");
      return false;
    }
  }, []);

  const stop = useCallback(
    ({ discard = false } = {}) =>
      new Promise((resolve) => {
        const cur = recRef.current;
        if (!cur) return resolve("");
        recRef.current = null;
        cur.unwatch?.();
        cur.rec.onstop = async () => {
          cur.stream.getTracks().forEach((t) => t.stop());
          recRef.current = null;
          setRecording(null);
          const blob = new Blob(cur.chunks, { type: cur.mime });
          if (discard || !blob.size) return resolve("");
          setTranscribing(true);
          try {
            const ext = cur.mime.includes("mp4") ? "mp4" : cur.mime.includes("ogg") ? "ogg" : "webm";
            const fd = new FormData();
            fd.append("audio", blob, `voice.${ext}`);
            const res = await fetch(`${API}/ai/voice/transcribe`, { method: "POST", body: fd });
            if (!res.ok) throw new Error();
            const { text } = await res.json();
            if (!text) toast.message("I didn't catch that — please try again.");
            resolve(text || "");
          } catch {
            toast.error("Couldn't transcribe audio. Please try again.");
            resolve("");
          } finally {
            setTranscribing(false);
          }
        };
        cur.rec.stop();
      }),
    []
  );

  const stopSpeaking = useCallback(() => {
    audioRef.current?.pause();
    audioRef.current = null;
    setSpeaking(false);
  }, []);

  const speak = useCallback(async (text) => {
    if (!text?.trim()) return;
    try {
      const res = await fetch(`${API}/ai/voice/speak`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ text }),
      });
      if (!res.ok) throw new Error();
      const { url } = await res.json();
      const audio = new Audio(`${API}${url.replace(/^\/api/, "")}`);
      audioRef.current = audio;
      audio.onplaying = () => setSpeaking(true);
      audio.onended = audio.onerror = () => setSpeaking(false);
      await audio.play();
    } catch {
      setSpeaking(false);
      toast.error("Luchii couldn't speak this reply.");
    }
  }, []);

  return { recording, transcribing, speaking, start, stop, speak, stopSpeaking };
}
